import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  createBuildingShaftGeometry,
  createBuildingShaftGeometryWithSegments,
} from './buildingFacades';

/** Distinct procedural shells instanced across the city (facade segments + trim layout). */
export const BUILDING_PROCEDURAL_VARIANTS = 3;

function pushMergedBox(
  list: THREE.BufferGeometry[],
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
): void {
  const g = new THREE.BoxGeometry(sx, sy, sz, 1, 1, 1);
  g.translate(cx, cy, cz);
  list.push(g);
}

function pushBelt(
  list: THREE.BufferGeometry[],
  y: number,
  face: 'north' | 'south' | 'east' | 'west',
  thickness = 0.028,
  depth = 0.04,
  hw = 0.48,
  hh = 0.018,
): void {
  if (face === 'north') {
    pushMergedBox(list, 0, y, 0.5 + depth * 0.5, hw * 2, hh * 2, thickness);
  } else if (face === 'south') {
    pushMergedBox(list, 0, y, -0.5 - depth * 0.5, hw * 2, hh * 2, thickness);
  } else if (face === 'east') {
    pushMergedBox(list, 0.5 + depth * 0.5, y, 0, thickness, hh * 2, hw * 2);
  } else {
    pushMergedBox(list, -0.5 - depth * 0.5, y, 0, thickness, hh * 2, hw * 2);
  }
}

function pushMullion(
  list: THREE.BufferGeometry[],
  x: number,
  z: number,
  face: 'ew' | 'ns',
  halfW: number,
  depth: number,
  h = 0.96,
): void {
  if (face === 'ew') {
    pushMergedBox(list, x, 0, z, halfW * 2, h, depth);
  } else {
    pushMergedBox(list, x, 0, z, depth, h, halfW * 2);
  }
}

function mergeParts(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const merged = mergeGeometries(parts, false);
  if (!merged) {
    return new THREE.BoxGeometry(0.01, 0.01, 0.01);
  }
  merged.computeBoundingSphere();
  return merged;
}

/**
 * Variant A — “Grid tower”: corner quoins, retail bulkhead, dense mullions, belt courses.
 */
function createTrimVariantGridTower(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  const corners: [number, number][] = [
    [-0.46, -0.46],
    [0.46, -0.46],
    [-0.46, 0.46],
    [0.46, 0.46],
  ];
  for (const [cx, cz] of corners) {
    pushMergedBox(parts, cx, 0, cz, 0.08, 1.04, 0.08);
  }

  for (const f of ['north', 'south', 'east', 'west'] as const) {
    if (f === 'north') {
      pushMergedBox(parts, 0, -0.38, 0.52, 0.92, 0.2, 0.055);
    } else if (f === 'south') {
      pushMergedBox(parts, 0, -0.38, -0.52, 0.92, 0.2, 0.055);
    } else if (f === 'east') {
      pushMergedBox(parts, 0.52, -0.38, 0, 0.055, 0.2, 0.92);
    } else {
      pushMergedBox(parts, -0.52, -0.38, 0, 0.055, 0.2, 0.92);
    }
  }

  const beltYs = [-0.22, -0.06, 0.1, 0.26, 0.4];
  for (const y of beltYs) {
    for (const f of ['north', 'south', 'east', 'west'] as const) {
      pushBelt(parts, y, f);
    }
  }

  const mx = [-0.32, -0.16, 0, 0.16, 0.32];
  for (const x of mx) {
    pushMullion(parts, x, 0.502, 'ew', 0.015, 0.024);
    pushMullion(parts, x, -0.502, 'ew', 0.015, 0.024);
  }
  const mz = [-0.32, -0.16, 0, 0.16, 0.32];
  for (const z of mz) {
    pushMullion(parts, 0.502, z, 'ns', 0.015, 0.024);
    pushMullion(parts, -0.502, z, 'ns', 0.015, 0.024);
  }

  pushMergedBox(parts, 0, 0.52, 0, 1.06, 0.045, 1.06);
  return mergeParts(parts);
}

/**
 * Variant B — “Ribbon tower”: lighter corners, more horizontal bands, wider window bays (fewer mullions).
 */
function createTrimVariantRibbon(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  for (const [cx, cz] of [
    [-0.47, -0.47],
    [0.47, -0.47],
    [-0.47, 0.47],
    [0.47, 0.47],
  ] as [number, number][]) {
    pushMergedBox(parts, cx, 0, cz, 0.055, 1.02, 0.055);
  }

  for (const f of ['north', 'south', 'east', 'west'] as const) {
    if (f === 'north') {
      pushMergedBox(parts, 0, -0.36, 0.515, 0.94, 0.16, 0.042);
    } else if (f === 'south') {
      pushMergedBox(parts, 0, -0.36, -0.515, 0.94, 0.16, 0.042);
    } else if (f === 'east') {
      pushMergedBox(parts, 0.515, -0.36, 0, 0.042, 0.16, 0.94);
    } else {
      pushMergedBox(parts, -0.515, -0.36, 0, 0.042, 0.16, 0.94);
    }
  }

  const beltYs = [-0.34, -0.22, -0.08, 0.06, 0.2, 0.32, 0.44];
  for (const y of beltYs) {
    for (const f of ['north', 'south', 'east', 'west'] as const) {
      pushBelt(parts, y, f, 0.022, 0.035, 0.49, 0.014);
    }
  }

  const mx = [-0.28, 0, 0.28];
  for (const x of mx) {
    pushMullion(parts, x, 0.504, 'ew', 0.018, 0.028);
    pushMullion(parts, x, -0.504, 'ew', 0.018, 0.028);
  }
  const mz = [-0.28, 0, 0.28];
  for (const z of mz) {
    pushMullion(parts, 0.504, z, 'ns', 0.018, 0.028);
    pushMullion(parts, -0.504, z, 'ns', 0.018, 0.028);
  }

  pushMergedBox(parts, 0, 0.515, 0, 1.04, 0.038, 1.04);
  return mergeParts(parts);
}

/**
 * Variant C — “Industrial slab”: thick horizontal bands, wide pilasters, heavy corners.
 */
function createTrimVariantIndustrial(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  for (const [cx, cz] of [
    [-0.44, -0.44],
    [0.44, -0.44],
    [-0.44, 0.44],
    [0.44, 0.44],
  ] as [number, number][]) {
    pushMergedBox(parts, cx, 0, cz, 0.11, 1.06, 0.11);
  }

  for (const f of ['north', 'south', 'east', 'west'] as const) {
    if (f === 'north') {
      pushMergedBox(parts, 0, -0.4, 0.53, 0.88, 0.24, 0.06);
    } else if (f === 'south') {
      pushMergedBox(parts, 0, -0.4, -0.53, 0.88, 0.24, 0.06);
    } else if (f === 'east') {
      pushMergedBox(parts, 0.53, -0.4, 0, 0.06, 0.24, 0.88);
    } else {
      pushMergedBox(parts, -0.53, -0.4, 0, 0.06, 0.24, 0.88);
    }
  }

  const beltYs = [-0.28, -0.12, 0.04, 0.22, 0.38];
  for (const y of beltYs) {
    for (const f of ['north', 'south', 'east', 'west'] as const) {
      pushBelt(parts, y, f, 0.038, 0.055, 0.46, 0.028);
    }
  }

  const mx = [-0.3, -0.1, 0.1, 0.3];
  for (const x of mx) {
    pushMullion(parts, x, 0.505, 'ew', 0.022, 0.032);
    pushMullion(parts, x, -0.505, 'ew', 0.022, 0.032);
  }
  const mz = [-0.3, -0.1, 0.1, 0.3];
  for (const z of mz) {
    pushMullion(parts, 0.505, z, 'ns', 0.022, 0.032);
    pushMullion(parts, -0.505, z, 'ns', 0.022, 0.032);
  }

  pushMergedBox(parts, 0, 0.535, 0, 1.08, 0.055, 1.08);
  return mergeParts(parts);
}

export type ProceduralTowerPair = {
  facade: THREE.BufferGeometry;
  trim: THREE.BufferGeometry;
};

export function createProceduralTowerGeometryPair(variantIndex: number): ProceduralTowerPair {
  const v =
    ((variantIndex % BUILDING_PROCEDURAL_VARIANTS) + BUILDING_PROCEDURAL_VARIANTS) %
    BUILDING_PROCEDURAL_VARIANTS;

  let facade: THREE.BoxGeometry;
  let trim: THREE.BufferGeometry;

  if (v === 0) {
    facade = createBuildingShaftGeometry();
    trim = createTrimVariantGridTower();
  } else if (v === 1) {
    facade = createBuildingShaftGeometryWithSegments(52);
    trim = createTrimVariantRibbon();
  } else {
    facade = createBuildingShaftGeometryWithSegments(28);
    trim = createTrimVariantIndustrial();
  }

  return { facade, trim };
}
